"use client";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/context/AuthContext";
import { getTalentsApplicationsByUserRole } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Profile } from "../../talent/types";
import BaseTalentsList from "./BaseTalentsList";

const MaybeInviteCandidateToPartnerButton = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation(["applications"]);

  if (profile.role !== "partner") return null;

  return (
    <Button
      onClick={() => {
        /*TODO: Copy profile.partner_id to clipboard*/
        navigator.clipboard.writeText(
          `${window.location.origin}/signup/talent?partner=${profile.partner_id}`
        );
        toast({
          title: t("messages.partner_id_copied"),
          description: t("messages.partner_id_copied_description")
        });
      }}
      variant="default"
    >
      {t("messages.invite_candidate")}
    </Button>
  );
};

export default function PartnerTalentsList() {
  const [talentProfiles, setTalentProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { profile } = useAuth();
  const { t } = useTranslation(["applications"]);

  useEffect(() => {
    async function fetchTalentProfiles() {
      try {
        if (profile.anonymous) {
          console.error("No user found");
          throw new Error("No user found");
        }
        const profiles = await getTalentsApplicationsByUserRole(
          profile.id,
          "partner"
        );
        setTalentProfiles(profiles);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    }
    if (!profile.anonymous) {
      fetchTalentProfiles();
    }
  }, [profile, t]);

  return (
    <div className="flex h-screen bg-white">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-0">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold">
                {t("titles.partner_talent_profiles")}
              </h1>
              <p className="text-gray-500">
                {t("descriptions.partner_talent_profiles")}
              </p>
            </div>
            <MaybeInviteCandidateToPartnerButton />
          </div>
        </div>

        {/* Content */}
        <BaseTalentsList
          talentProfiles={talentProfiles}
          loading={loading}
          error={error}
        />
      </div>
    </div>
  );
}
